// ModelCard.tsx — the Viewer's splat model: WebGL2 view with orbit/touch
// controls, an Object only / All toggle, and the two-tap measure tool. Each
// tap picks the nearest gaussian on the tap ray; after the second tap both
// points go to /measure (kind "distance", snap) and the server's snapped
// points and distance replace the local picks. The camera path is drawn over
// the model (toggle "Path"); the active camera shows its frustum, and tapping
// a camera dot selects it for the scan visualization below. Once the path
// arrives the view re-frames once to include the cameras. A tapTool (guided
// segmentation's seeding and 3D box) takes over taps from both and shows
// every label.
// A §16 cloud with material (OSPL v2) adds a Captured / Neutral / Albedo
// shading control (splat/shading.ts) and the scan's ambient-light chip.
import { useMemo, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import { BsCameraVideo, BsRulers, BsX } from 'react-icons/bs';
import { api } from '../api';
import type { Scan } from '../api';
import { SplatView } from '../components/SplatView';
import type { OverlayBox, SplatPath, SplatViewProps } from '../components/SplatView';
import { errorMessage, fmtCount, fmtMm } from '../format';
import { LABEL_OBJECT } from '../splat/format';
import { SHADING_MODES, ambientChip, shadeCloud } from '../splat/shading';
import type { ShadingMode } from '../splat/shading';
import type { SplatCloud } from '../splat/format';
import type { Vec3 } from '../splat/math';
import { vec3Dist } from '../splat/math';
import type { PickHit } from '../splat/pick';
import { frustumCorners } from '../splat/camera';
import type { PathCamera } from './trajectory';

export interface ModelCardProps {
  scan: Scan;
  cloud: SplatCloud | null;
  path?: PathCamera[];
  activeCamera?: number; // index into path
  onActiveCamera?: (i: number) => void;
  tapTool?: TapTool | null;
}

// TapTool is another tool's claim on taps on the model, with the hint shown
// in the footer while it is active, plus optional boxes to draw and a grab
// hook for dragging them (guided segmentation's 3D box).
export interface TapTool {
  hint: string;
  onPick: (hit: PickHit | null) => void;
  boxes?: readonly OverlayBox[];
  onGrab?: SplatViewProps['onGrab'];
}

const SHADING_TITLES: Record<ShadingMode, string> = {
  captured: 'Colours as the room lit them',
  neutral: 'Surface colour under neutral white light',
  albedo: 'Surface colour without any lighting',
};

// frustumDepth sizes the drawn frustum to the path: a quarter of the cameras'
// mean distance from their centroid (≥ 1 cm).
function frustumDepth(path: PathCamera[]): number {
  const c = [0, 0, 0];
  for (const p of path) for (let k = 0; k < 3; k++) c[k] += p.center[k] / path.length;
  let d = 0;
  for (const p of path) d += Math.hypot(p.center[0] - c[0], p.center[1] - c[1], p.center[2] - c[2]) / path.length;
  return Math.max(0.01, 0.25 * d);
}

export function ModelCard({ scan, cloud, path = [], activeCamera, onActiveCamera, tapTool }: ModelCardProps) {
  const [showPath, setShowPath] = useState(true);
  const [objectOnly, setObjectOnly] = useState(true);
  const [measuring, setMeasuring] = useState(false);
  const [points, setPoints] = useState<Vec3[]>([]);
  const [distanceMm, setDistanceMm] = useState<number | null>(null);
  const [status, setStatus] = useState('');
  const [shading, setShading] = useState<ShadingMode>('captured');
  const hasMaterial = !!cloud?.material;
  // Guided tinting (tapTool) keeps its colours; shading applies otherwise.
  const shaded = useMemo(() => {
    if (!cloud || !hasMaterial || shading === 'captured' || tapTool) return cloud;
    const t0 = performance.now();
    const c = shadeCloud(cloud, shading, scan.lighting);
    console.info(`[openscan/viewer] shading ${shading}: ${c.count} gaussians in ${Math.round(performance.now() - t0)} ms`);
    return c;
  }, [cloud, hasMaterial, shading, tapTool, scan.lighting]);
  const chip = ambientChip(scan.lighting);

  const objectCount = useMemo(() => {
    if (!cloud) return 0;
    let n = 0;
    for (let i = 0; i < cloud.count; i++) if (cloud.label[i] === LABEL_OBJECT) n++;
    return n;
  }, [cloud]);
  const centers = useMemo(() => path.map((p) => p.center), [path]);
  const depth = useMemo(() => (path.length ? frustumDepth(path) : 0), [path]);
  const splatPath = useMemo<SplatPath | null>(() => {
    if (!showPath || centers.length === 0) return null;
    const cam = activeCamera != null ? path[activeCamera] : undefined;
    return { centers, active: activeCamera, frustum: cam ? frustumCorners(cam.pose, cam.intrinsics, depth) : null };
  }, [showPath, centers, path, activeCamera, depth]);

  // Before segmentation nothing is labelled Object; show everything then.
  const labels = !tapTool && objectOnly && objectCount > 0 ? [LABEL_OBJECT] : null;

  const onPick = async (hit: PickHit | null) => {
    if (!measuring) return;
    if (!hit) {
      setStatus('No surface under that tap — try again on the object.');
      return;
    }
    const next = points.length >= 2 ? [hit.point] : [...points, hit.point];
    setPoints(next);
    setDistanceMm(null);
    if (next.length < 2) {
      setStatus('First point set. Tap the second point.');
      return;
    }
    const local = vec3Dist(next[0], next[1]) * 1000;
    setDistanceMm(local);
    setStatus('Snapping on the server…');
    try {
      const r = await api.measureDistance(scan.id, next[0], next[1], true);
      console.info(`[openscan/measure] ${r.distance_mm.toFixed(2)} mm (local ${local.toFixed(2)} mm)`);
      setPoints([r.a, r.b]);
      setDistanceMm(r.distance_mm);
      setStatus('Snapped to the surface.');
    } catch (err) {
      setStatus(`Server snap failed (${errorMessage(err)}); showing the unsnapped distance.`);
    }
  };

  const clear = () => {
    setPoints([]);
    setDistanceMm(null);
    setStatus('');
  };

  return (
    <Card>
      <Card.Header className="d-flex flex-wrap align-items-center gap-2">
        <span className="me-auto">Model</span>
        <span className="small text-body-secondary">{cloud ? `${fmtCount(cloud.count)} splats` : ''}</span>
        {hasMaterial && (
          <ButtonGroup size="sm" aria-label="Shading">
            {SHADING_MODES.map((m) => (
              <Button key={m} variant={shading === m ? 'primary' : 'outline-primary'} onClick={() => setShading(m)} title={SHADING_TITLES[m]}>
                {m[0].toUpperCase() + m.slice(1)}
              </Button>
            ))}
          </ButtonGroup>
        )}
        <ButtonGroup size="sm">
          <Button variant={objectOnly ? 'primary' : 'outline-primary'} onClick={() => setObjectOnly(true)}>
            Object only
          </Button>
          <Button variant={objectOnly ? 'outline-primary' : 'primary'} onClick={() => setObjectOnly(false)}>
            All
          </Button>
        </ButtonGroup>
      </Card.Header>
      <Card.Body className="p-0">
        <SplatView
          cloud={shaded}
          labels={labels}
          height="62vh"
          fitKey={`${scan.id}:${centers.length > 0 ? 'path' : 'model'}`}
          fitPoints={centers}
          points={points}
          onPick={tapTool ? tapTool.onPick : measuring ? onPick : undefined}
          path={splatPath}
          onPathPick={!tapTool && !measuring && splatPath ? onActiveCamera : undefined}
          boxes={tapTool?.boxes}
          onGrab={tapTool?.onGrab}
          emptyText={scan.status === 'capturing' ? 'Waiting for the first seed from the phone…' : 'No splats yet'}
        />
      </Card.Body>
      <Card.Footer className="d-flex flex-wrap align-items-center gap-2">
        <Button
          size="sm"
          variant={measuring ? 'secondary' : 'outline-secondary'}
          onClick={() => {
            setMeasuring(!measuring);
            setStatus(measuring ? '' : 'Tap two points on the model.');
          }}
        >
          <BsRulers className="me-1" />
          {measuring ? 'Measuring' : 'Measure'}
        </Button>
        {path.length > 0 && (
          <Button
            size="sm"
            variant={showPath ? 'secondary' : 'outline-secondary'}
            onClick={() => setShowPath(!showPath)}
            aria-pressed={showPath}
            title="Show the camera path over the model"
          >
            <BsCameraVideo className="me-1" />
            Path
          </Button>
        )}
        {points.length > 0 && (
          <Button size="sm" variant="link" onClick={clear}>
            <BsX className="me-1" />
            Clear
          </Button>
        )}
        {chip && <span className="small text-body-secondary">{chip}</span>}
        {distanceMm != null && <span className="measure-value fs-5">{fmtMm(distanceMm, 2)}</span>}
        <span className="small text-body-secondary">{tapTool ? tapTool.hint : status}</span>
        {!tapTool && objectOnly && objectCount === 0 && cloud && cloud.count > 0 && (
          <span className="small text-body-secondary ms-auto">Not segmented yet — showing everything.</span>
        )}
      </Card.Footer>
    </Card>
  );
}
